import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ConversationList from '../components/conversations/ConversationList';
import ChatWindow from '../components/conversations/ChatWindow';
import ContactPanel from '../components/conversations/ContactPanel';
import SalesCard from '../components/conversations/SalesCard';
import ApiService from '../services/api';
import RealtimeService from '../services/realtime';
import { playNotification } from '../services/notificationSound';
import { useIsMobile } from '../../hooks/useIsMobile';
import './CRMPage.css';
import pageCache from '../services/pageCache';

function CRMPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const selectedConversationRef = useRef(null);
  const pendingContactId = useRef(searchParams.get('contact_id'));

  // Atualiza pendingContactId quando URL muda (componente já montado).
  // Se conversations já carregou, executa seleção diretamente — sem depender
  // do useEffect [conversations] que só dispara quando a lista muda.
  useEffect(() => {
    const contactId = searchParams.get('contact_id');
    if (!contactId) return;
    console.log("🎯 deep-link disparou:", contactId, "conversations:", conversations.length);
    pendingContactId.current = contactId;
    if (conversations.length === 0) return; // useEffect [conversations] vai disparar quando carregar
    const match = conversations.find(c => c.contact?.id === contactId);
    if (match) {
      pendingContactId.current = null;
      searchParams.delete('contact_id');
      setSearchParams(searchParams, { replace: true });
      handleSelectConversation(match, false);
      if (isMobile) setMobileViewSafe('chat');
    } else {
      const cid = contactId;
      pendingContactId.current = null;
      searchParams.delete('contact_id');
      setSearchParams(searchParams, { replace: true });
      ApiService.getConversations('default', '', cid).then(data => {
        if (data && data.length > 0) {
          setConversations(prev => [data[0], ...prev.filter(c => c.id !== data[0].id)]);
          handleSelectConversation(data[0], false);
          if (isMobile) setMobileViewSafe('chat');
        }
      }).catch(err => console.error('Deep-link fetch error:', err));
    }
  }, [searchParams]);
  const [messages, setMessages] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const searchTermRef = useRef('');
  const searchTimerRef = useRef(null);
  const [globalSearchResults, setGlobalSearchResults] = useState(null);
  const [globalSearching, setGlobalSearching] = useState(false);

  const handleSearch = (term) => {
    setSearchTerm(term);
    searchTermRef.current = term;

    // Cancela timer anterior
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);

    // Sem termo suficiente: limpa resultados e recarrega inbox
    if (!term || term.trim().length < 2) {
      setGlobalSearchResults(null);
      setGlobalSearching(false);
      loadConversations('');
      return;
    }

    // Um único debounce dispara busca de conversas + busca global em paralelo
    setGlobalSearching(true);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const [, results] = await Promise.all([
          loadConversations(term),
          ApiService.globalSearch(term),
        ]);
        setGlobalSearchResults(results);
      } catch (e) {
        console.error('Erro na busca global:', e);
        setGlobalSearchResults({ contacts: [], messages: [] });
      } finally {
        setGlobalSearching(false);
      }
    }, 400);
  };

  const handleSelectSearchResult = (result) => {
    // Limpa o campo de busca e fecha o dropdown
    handleSearch('');

    if (!result?.conversation_id) return;

    // Buscar a conversa na lista local ou carregar pelo ID
    const existing = conversations.find(c => c.id === result.conversation_id);
    if (existing) {
      handleSelectConversation(existing);
    } else {
      // Conversa não está na lista (ex: arquivada) — busca pelo contact_id
      ApiService.getConversations('all', '', result.contact_id).then(data => {
        if (data && data.length > 0) {
          setConversations(prev => [data[0], ...prev.filter(c => c.id !== data[0].id)]);
          handleSelectConversation(data[0]);
        }
      }).catch(err => console.error('Erro ao abrir conversa do resultado:', err));
    }
    if (isMobile) setMobileViewSafe('chat');
  };
  
  // SalesCard
  const [salesCardContact, setSalesCardContact] = useState(null);
  
  // ContactPanel
  const [showContactPanel, setShowContactPanel] = useState(false);

  // Toast de arquivamento (feedback "Desfazer")
  const [archiveToast, setArchiveToast] = useState(null);
  const archiveToastTimer = useRef(null);

  // Mobile
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState('list'); // 'list' | 'chat'
  const mobileViewRef = useRef('list');

  const setMobileViewSafe = useCallback((view) => {
    mobileViewRef.current = view;
    setMobileView(view);
  }, []);

  const isFirstLoad = useRef(true);
  const refreshTimer = useRef(null);

  const loadConversations = useCallback(async (search = '') => {
    const CACHE_KEY = 'crm/conversations';

    // Stale-while-revalidate: se não é busca textual e há cache válido,
    // mostra imediatamente sem loading e revalida em background.
    if (!search && isFirstLoad.current) {
      const cached = pageCache.get(CACHE_KEY);
      if (cached) {
        setConversations(cached);
        setLoading(false);
        isFirstLoad.current = false;
        // Revalida em background — sem loading visível
        ApiService.getConversations('default', '').then(data => {
          pageCache.set(CACHE_KEY, data);
          setConversations(prev => {
            const sel = selectedConversationRef.current;
            if (sel && !data.find(c => c.id === sel.id)) {
              const kept = prev.find(c => c.id === sel.id);
              if (kept) return [kept, ...data];
            }
            return data;
          });
        }).catch(err => console.error('Revalidação em background falhou:', err));
        return;
      }
    }

    try {
      if (isFirstLoad.current) {
        setLoading(true);
      }

      const data = await ApiService.getConversations('default', search);

      // Só cacheia inbox principal (sem busca textual)
      if (!search) {
        pageCache.set(CACHE_KEY, data);
      }

      setConversations(prev => {
        // Preservar conversa selecionada que não está nos 50 mais recentes
        const sel = selectedConversationRef.current;
        if (sel && !data.find(c => c.id === sel.id)) {
          const kept = prev.find(c => c.id === sel.id);
          if (kept) return [kept, ...data];
        }
        return data;
      });

      if (isFirstLoad.current) {
        isFirstLoad.current = false;
      }
    } catch (error) {
      console.error('Erro ao carregar conversas:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const debouncedRefresh = useCallback(() => {
    if (refreshTimer.current) {
      clearTimeout(refreshTimer.current);
    }
    refreshTimer.current = setTimeout(() => {
      loadConversations(searchTermRef.current);
    }, 500);
  }, [loadConversations]);

  useEffect(() => {
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current);
    };
  }, []);

  const loadMessages = useCallback(async (conversationId) => {
    try {
      const data = await ApiService.getMessages(conversationId, 50);
      setMessages(data.messages || data);
      setHasMore(data.has_more || false);
      await ApiService.markConversationAsRead(conversationId);
      setConversations((prev) =>
        prev.map((conv) =>
          conv.id === conversationId ? { ...conv, unread_count: 0 } : conv
        )
      );
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
    }
  }, []);

  const loadMoreMessages = useCallback(async () => {
    if (loadingMore || !hasMore || !selectedConversation || messages.length === 0) return;

    setLoadingMore(true);
    try {
      const oldestTimestamp = messages[0]?.created_at;
      const data = await ApiService.getMessages(selectedConversation.id, 50, oldestTimestamp);
      const olderMessages = data.messages || data;

      if (olderMessages.length > 0) {
        setMessages((prev) => [...olderMessages, ...prev]);
      }
      setHasMore(data.has_more || false);
    } catch (error) {
      console.error('Erro ao carregar mais mensagens:', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, hasMore, selectedConversation, messages]);

  const handleSelectConversation = useCallback((conversation, fromUserTap = true) => {
    if (!conversation) return;
    setSelectedConversation(conversation);
    selectedConversationRef.current = conversation;
    localStorage.setItem('lastSelectedConversationId', conversation.id);
    loadMessages(conversation.id);
    if (isMobile && fromUserTap) setMobileViewSafe('chat');
  }, [loadMessages, isMobile, setMobileViewSafe]);

  const handleDoubleClickConversation = useCallback((conversation) => {
    if (!conversation?.contact) return;
    setSalesCardContact({
      id: conversation.contact.id,
      name: conversation.contact.name
    });
  }, []);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Deep-link: contact_id from Dashboard → força seleção
  useEffect(() => {
    console.log("🔍 deep-link conversations check:", pendingContactId.current, conversations.length);
    if (!pendingContactId.current || conversations.length === 0) return;
    const match = conversations.find(c => c.contact?.id === pendingContactId.current);
    if (match) {
      pendingContactId.current = null;
      searchParams.delete('contact_id');
      setSearchParams(searchParams, { replace: true });
      handleSelectConversation(match, false);
      if (isMobile) setMobileViewSafe('chat');
    } else {
      // Conversa não está nas 50 mais recentes — buscar direto pelo contact_id
      const contactId = pendingContactId.current;
      pendingContactId.current = null;
      searchParams.delete('contact_id');
      setSearchParams(searchParams, { replace: true });
      ApiService.getConversations('all', '', contactId).then(data => {
        if (data && data.length > 0) {
          setConversations(prev => [data[0], ...prev.filter(c => c.id !== data[0].id)]);
          handleSelectConversation(data[0], false);
          if (isMobile) setMobileViewSafe('chat');
        }
      }).catch(err => console.error('Deep-link fetch error:', err));
    }
  }, [conversations]);

  // Auto-select default (só se NÃO veio do Dashboard)
  useEffect(() => {
    if (conversations.length > 0 && !selectedConversation && !pendingContactId.current) {
      // Mobile: não auto-seleciona, vendedor toca pra abrir
      if (isMobile) return;
      const lastConvId = localStorage.getItem('lastSelectedConversationId');
      if (lastConvId) {
        const found = conversations.find(c => c.id === lastConvId);
        if (found) {
          handleSelectConversation(found, false);
          return;
        }
      }
      handleSelectConversation(conversations[0], false);
    }
  }, [conversations, selectedConversation, handleSelectConversation, isMobile]);

  // SYNC selectedConversation com dados frescos (sem re-render no mobile chat)
  useEffect(() => {
    if (selectedConversation && conversations.length > 0) {
      const fresh = conversations.find(c => c.id === selectedConversation.id);
      if (fresh && (
        fresh.agent_suggested_reply !== selectedConversation.agent_suggested_reply ||
        fresh.agent_suggestion_used !== selectedConversation.agent_suggestion_used
      )) {
        // No mobile em chat, não trocar referência para evitar flicker
        if (isMobile && mobileViewRef.current === 'chat') return;
        setSelectedConversation(fresh);
      }
    }
  }, [conversations, isMobile]);

  // Realtime: conversation updates
  useEffect(() => {
    const subscription = RealtimeService.subscribeToConversations(
      'default',
      (payload) => {
        // Toca som para qualquer UPDATE inbound em conversa que não está aberta
        const updated = payload?.new;
        const isOtherConv = updated?.id !== selectedConversationRef.current?.id;
        const isInbound = (
          updated?.last_message_direction === 'inbound' ||
          updated?.unread_count > 0
        );
        if (isOtherConv && isInbound) {
          playNotification();
        }
        debouncedRefresh();
      }
    );
    return () => { RealtimeService.unsubscribeAll(); };
  }, [debouncedRefresh]);

  // Realtime: novas conversas
  useEffect(() => {
    const subscription = RealtimeService.subscribeToNewConversations(
      'default',
      () => debouncedRefresh()
    );
    return () => { subscription?.unsubscribe(); };
  }, [debouncedRefresh]);

  // Realtime: novas mensagens na conversa aberta
  useEffect(() => {
    if (selectedConversation) {
      const subscription = RealtimeService.subscribeToMessages(
        selectedConversation.id,
        (newMessage) => {
          if (newMessage.conversation_id === selectedConversation?.id) {
            // Toca som se a mensagem é inbound (do cliente) e a aba está em segundo plano
            if (newMessage.direction === 'inbound' && document.hidden) {
              playNotification();
            }
            setMessages((prev) => {
              const exists = prev.some((m) => m.id === newMessage.id);
              if (exists) return prev;
              return [...prev, newMessage];
            });
            ApiService.markConversationAsRead(newMessage.conversation_id);
          }
          debouncedRefresh();
        }
      );
      return () => { subscription?.unsubscribe(); };
    }
  }, [selectedConversation, debouncedRefresh]);

  // Reconexão após inatividade — cobre o gap do WebSocket suspenso
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        debouncedRefresh();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [debouncedRefresh]);

  // ─── Arquivamento ───

  /**
   * Arquiva uma conversa: chama backend, remove da lista local,
   * e mostra toast com opção de desfazer por 5 segundos.
   */
  const handleArchiveConversation = useCallback(async (conversation) => {
    if (!conversation?.id) return;

    // Optimistic update — remove da lista imediatamente
    setConversations((prev) => prev.filter((c) => c.id !== conversation.id));

    // Se a conversa arquivada era a selecionada, deseleciona
    if (selectedConversationRef.current?.id === conversation.id) {
      setSelectedConversation(null);
      selectedConversationRef.current = null;
      setMessages([]);
    }

    try {
      await ApiService.archiveConversation(conversation.id);

      // Mostrar toast com "Desfazer"
      const contactName = conversation.contact?.name || 'contato';
      setArchiveToast({
        id: conversation.id,
        message: `Conversa de ${contactName} arquivada`,
        conversation, // guardar referência pra restaurar no desfazer
      });

      // Auto-some em 5s
      if (archiveToastTimer.current) clearTimeout(archiveToastTimer.current);
      archiveToastTimer.current = setTimeout(() => {
        setArchiveToast(null);
      }, 5000);
    } catch (err) {
      console.error('Erro ao arquivar conversa:', err);
      // Reverter otimismo: recarregar lista
      loadConversations(searchTermRef.current);
      alert('Erro ao arquivar conversa. Tente novamente.');
    }
  }, [loadConversations]);

  /**
   * Desfaz arquivamento (clique no toast "Desfazer").
   * Suporta tanto single quanto batch.
   */
  const handleUndoArchive = useCallback(async () => {
    const toast = archiveToast;
    if (!toast?.id) return;

    if (archiveToastTimer.current) {
      clearTimeout(archiveToastTimer.current);
      archiveToastTimer.current = null;
    }
    setArchiveToast(null);

    try {
      if (toast.isBatch && Array.isArray(toast.batchIds) && toast.batchIds.length > 0) {
        // Desfazer lote — uma chamada batch
        await ApiService.unarchiveBatch(toast.batchIds);
      } else {
        // Desfazer single
        await ApiService.unarchiveConversation(toast.id);
      }
      loadConversations(searchTermRef.current);
    } catch (err) {
      console.error('Erro ao desarquivar:', err);
      alert('Erro ao desfazer. Você pode buscar a conversa para reabri-la.');
    }
  }, [archiveToast, loadConversations]);

  /**
   * Clique em conversa arquivada (apareceu na busca):
   * desarquiva no backend, recarrega lista e abre.
   */
  const handleClickArchived = useCallback(async (conversation) => {
    if (!conversation?.id) return;

    try {
      await ApiService.unarchiveConversation(conversation.id);
      // Atualizar status local pra evitar flash visual
      const updated = { ...conversation, status: 'active' };
      setConversations((prev) => {
        const exists = prev.some((c) => c.id === updated.id);
        if (exists) {
          return prev.map((c) => (c.id === updated.id ? updated : c));
        }
        return [updated, ...prev];
      });
      handleSelectConversation(updated);
      // Refresh em segundo plano para sincronizar com servidor
      debouncedRefresh();
    } catch (err) {
      console.error('Erro ao reabrir conversa arquivada:', err);
      alert('Erro ao reabrir conversa.');
    }
  }, [handleSelectConversation, debouncedRefresh]);

  /**
   * Arquiva múltiplas conversas em uma única chamada (modo seleção).
   * Toast de "Desfazer" reverte o lote inteiro de uma vez.
   */
  const handleArchiveBatch = useCallback(async (conversationsToArchive) => {
    if (!Array.isArray(conversationsToArchive) || conversationsToArchive.length === 0) return;

    const ids = conversationsToArchive.map((c) => c.id);
    const count = ids.length;

    // Optimistic: remover da lista imediatamente
    const idsSet = new Set(ids);
    setConversations((prev) => prev.filter((c) => !idsSet.has(c.id)));

    // Se a conversa selecionada era uma das arquivadas, deseleciona
    if (selectedConversationRef.current?.id && idsSet.has(selectedConversationRef.current.id)) {
      setSelectedConversation(null);
      selectedConversationRef.current = null;
      setMessages([]);
    }

    try {
      const result = await ApiService.archiveBatch(ids);
      const archivedCount = result?.archived ?? count;

      // Toast com Desfazer (lote)
      const message = archivedCount === 1
        ? '1 conversa fechada'
        : `${archivedCount} conversas fechadas`;

      setArchiveToast({
        id: `batch-${Date.now()}`,
        message,
        batchIds: result?.ids || ids, // IDs efetivamente arquivados
        isBatch: true,
      });

      if (archiveToastTimer.current) clearTimeout(archiveToastTimer.current);
      archiveToastTimer.current = setTimeout(() => {
        setArchiveToast(null);
      }, 5000);
    } catch (err) {
      console.error('Erro ao arquivar em lote:', err);
      // Reverter otimismo: recarregar lista
      loadConversations(searchTermRef.current);
      alert(`Erro ao fechar ${count} conversas. Tente novamente.`);
    }
  }, [loadConversations]);

  // Cleanup timer do toast ao desmontar
  useEffect(() => {
    return () => {
      if (archiveToastTimer.current) clearTimeout(archiveToastTimer.current);
    };
  }, []);

  const handleSendMessage = async (messageData) => {
    if (!selectedConversation) return;
    try {
      if (typeof messageData === 'string') {
        await ApiService.sendMessage(selectedConversation.id, messageData);
      } else {
        const { content, media_url, media_type, media_filename, media_mimetype, quoted_message_id } = messageData;
        await ApiService.sendMessage(
          selectedConversation.id, content,
          media_url, media_type, media_filename, media_mimetype,
          quoted_message_id
        );
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      alert('Erro ao enviar mensagem!');
    }
  };

  const filteredConversations = conversations;

  return (
    <div className="crm-page">
      <div className="crm-container">
        <div className={`conversations-panel${isMobile && mobileView !== 'list' ? ' mobile-hidden' : ''}`}>
          <ConversationList
            conversations={filteredConversations}
            selectedConversation={selectedConversation}
            onSelectConversation={handleSelectConversation}
            onDoubleClickConversation={handleDoubleClickConversation}
            onSearch={handleSearch}
            searchResults={globalSearchResults}
            onSelectResult={handleSelectSearchResult}
            searching={globalSearching}
            loading={loading}
            onArchive={handleArchiveConversation}
            onClickArchived={handleClickArchived}
            onArchiveBatch={handleArchiveBatch}
            onConversationCreated={(conversation) => {
              pageCache.invalidate('crm/conversations');
              loadConversations().then(() => {
                handleSelectConversation(conversation);
              });
            }}
          />
        </div>

        <div className={`chat-panel${isMobile && mobileView !== 'chat' ? ' mobile-hidden' : ''}`}>
          {selectedConversation ? (
            <ChatWindow
              conversation={selectedConversation}
              messages={messages}
              onSendMessage={handleSendMessage}
              hasMore={hasMore}
              loadingMore={loadingMore}
              onLoadMore={loadMoreMessages}
              onContactClick={() => setShowContactPanel(!showContactPanel)}
              onBack={isMobile ? () => setMobileViewSafe('list') : undefined}
            />
          ) : (
            <div className="empty-chat">
              <p>Selecione uma conversa para começar</p>
            </div>
          )}
        </div>

        {/* Contact Panel - sidebar direita */}
        {showContactPanel && selectedConversation?.contact && (
          <ContactPanel
            contact={selectedConversation.contact}
            onClose={() => setShowContactPanel(false)}
            onContactUpdated={(updated) => {
              // Atualizar contato na conversa selecionada e na lista
              setSelectedConversation(prev => ({
                ...prev,
                contact: { ...prev.contact, ...updated }
              }));
              setConversations(prev =>
                prev.map(c =>
                  c.contact?.id === updated.id
                    ? { ...c, contact: { ...c.contact, ...updated } }
                    : c
                )
              );
            }}
          />
        )}
      </div>

      {/* SalesCard - abre com duplo-clique na lista de conversas */}
      {salesCardContact && (
        <SalesCard
          contactId={salesCardContact.id}
          contactName={salesCardContact.name}
          onClose={() => setSalesCardContact(null)}
        />
      )}

      {/* Toast de arquivamento — feedback com opção de desfazer */}
      {archiveToast && (
        <div className="archive-toast" role="status" aria-live="polite">
          <span>{archiveToast.message}</span>
          <button type="button" onClick={handleUndoArchive}>
            Desfazer
          </button>
        </div>
      )}
    </div>
  );
}

export default CRMPage;
